import { Button, DatePicker, Form, Select, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { datasetByKey } from "@/warRoom/datasets/registry";
import { labelOf } from "@/warRoom/graph/format";
import { lastNMonths, thisMonth, thisYear } from "@/warRoom/graph/dateRanges";
import {
  useGraphStore,
  useSelectedWidget,
} from "@/warRoom/store/useGraphStore";

const { RangePicker } = DatePicker;
const { Text } = Typography;

/** 期間快捷：一個下拉取代四顆按鈕＋清除鍵 */
const QUICK: {
  value: string;
  range: () => [string, string] | null;
}[] = [
  { value: "all", range: () => null },
  { value: "m1", range: thisMonth },
  { value: "m3", range: () => lastNMonths(3) },
  { value: "m12", range: () => lastNMonths(12) },
  { value: "y", range: thisYear },
  { value: "custom", range: () => null },
];

const matchQuick = (range: [string, string] | null) => {
  if (!range) return "all";
  const hit = QUICK.find((q) => {
    const r = q.range();
    return r && r[0] === range[0] && r[1] === range[1];
  });
  return hit?.value ?? "custom";
};

/**
 * 篩選只作用在「選中的那張圖」。
 * 畫布上每張圖各有自己的資料與期間，所以沒有跨畫布的共用篩選列 ——
 * 一條看起來管全部的篩選列，在多張圖的畫布上就是騙人。
 */
export default function FilterPanel() {
  const { t } = useTranslation();
  const widget = useSelectedWidget();
  const { setDateField, setDateRange, setDimFilter, clearDimFilters } =
    useGraphStore();
  const datasetKey = widget?.spec.datasetKey ?? null;
  const rows = useGraphStore((s) =>
    datasetKey ? s.rowsByDataset[datasetKey] : undefined,
  );
  // 選了「自訂…」但還沒挑日期的那一瞬間，下拉要停在自訂上
  const [custom, setCustom] = useState(false);

  const dataset = datasetKey ? datasetByKey(datasetKey) : undefined;
  const timeFields = dataset?.fields.filter((f) => f.role === "time") ?? [];
  const dimFields = useMemo(
    () =>
      dataset?.fields.filter(
        (f) => f.role === "dimension" && !f.highCardinality,
      ) ?? [],
    [dataset],
  );

  const distinct = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const f of dimFields) {
      const seen = new Set<string>();
      for (const r of rows ?? []) {
        const v = r[f.key];
        if (v === null || v === undefined || v === "") continue;
        seen.add(String(v));
        if (seen.size > 60) break;
      }
      map.set(
        f.key,
        [...seen].sort((a, b) => a.localeCompare(b, "zh-Hant")),
      );
    }
    return map;
  }, [dimFields, rows]);

  if (!widget || !dataset) return null;
  const { spec } = widget;

  const quick =
    custom && !spec.dateRange ? "custom" : matchQuick(spec.dateRange);
  const range: [Dayjs, Dayjs] | null = spec.dateRange
    ? [dayjs(spec.dateRange[0]), dayjs(spec.dateRange[1])]
    : null;
  const activeDims = dimFields.filter(
    (f) => (spec.dimFilters[f.key] ?? []).length > 0,
  );

  return (
    <Form layout="vertical" size="small" style={{ marginBottom: 0 }}>
      <Form.Item
        label={t("warRoom.filter.period")}
        style={{ marginBottom: 10 }}
      >
        {timeFields.length > 0 ? (
          <>
            <Select
              value={quick}
              onChange={(v) => {
                setCustom(v === "custom");
                if (v === "custom") return; // 日期交給下面的選擇器
                setDateRange(QUICK.find((x) => x.value === v)!.range());
              }}
              options={QUICK.map(({ value }) => ({
                value,
                label: t(`warRoom.filter.quick.${value}`),
              }))}
            />
            {quick === "custom" ? (
              <RangePicker
                style={{ width: "100%", marginTop: 6 }}
                value={range}
                onChange={(v) =>
                  setDateRange(
                    v && v[0] && v[1]
                      ? [v[0].format("YYYY-MM-DD"), v[1].format("YYYY-MM-DD")]
                      : null,
                  )
                }
              />
            ) : null}
            {/* 只有一個時間欄位時不用問「依哪個日期」—— 沒有第二個選項 */}
            {timeFields.length > 1 ? (
              <Select
                style={{ marginTop: 6 }}
                value={spec.dateField ?? timeFields[0].key}
                onChange={setDateField}
                options={timeFields.map((f) => ({
                  value: f.key,
                  label: t("warRoom.filter.byField", { field: f.label }),
                }))}
              />
            ) : (
              <Text type="secondary" className="field-hint">
                {t("warRoom.filter.byField", { field: timeFields[0].label })}
              </Text>
            )}
          </>
        ) : (
          <Text
            type="secondary"
            className="field-hint"
            style={{ marginTop: 0 }}
          >
            {t("warRoom.filter.noDateField")}
          </Text>
        )}
      </Form.Item>

      {dimFields.length > 0 ? (
        <Form.Item
          label={t("warRoom.filter.onlyValues")}
          style={{ marginBottom: 0 }}
        >
          {dimFields.slice(0, 3).map((f) => (
            <Select
              key={f.key}
              mode="multiple"
              allowClear
              maxTagCount={1}
              style={{ width: "100%", marginBottom: 6 }}
              placeholder={f.label}
              value={spec.dimFilters[f.key] ?? []}
              onChange={(v: string[]) => setDimFilter(f.key, v)}
              options={(distinct.get(f.key) ?? []).map((v) => ({
                value: v,
                label: labelOf(f, v),
              }))}
            />
          ))}
          {activeDims.length > 0 ? (
            <Button size="small" type="text" onClick={clearDimFilters}>
              {t("warRoom.filter.clear")}
            </Button>
          ) : null}
        </Form.Item>
      ) : null}
    </Form>
  );
}
